import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Hexagon, Play, Database, Table2, DollarSign, Activity, ArrowUpRight } from "lucide-react";
import { api, fmtMoney, fmtDateTime } from "@/lib/api";
import { StatCard, BentoCard, BrutalButton } from "@/components/dashboard/BentoCard";
import { HexBadge } from "@/components/dashboard/Hex";
import { DashboardAnalytics } from "@/components/dashboard/DashboardAnalytics";
import { DASHBOARD } from "@/constants/testIds";

export default function DashboardPage() {
  const qc = useQueryClient();

  const { data: stats } = useQuery({
    queryKey: ["stats"],
    queryFn: async () => (await api.get("/stats")).data,
    refetchInterval: 30000,
  });

  const runAll = useMutation({
    mutationFn: async () => (await api.post("/scrape/run", {})).data,
    onSuccess: (data) => {
      if (data.queued) {
        // Background run — the scheduler auto-refetches stats every 30s,
        // so the numbers on this page will tick up as sources finish.
        toast.success(
          data.message ||
          "Scrape started in the background. Check the Runs page in ~2 minutes.",
        );
        // Kick a delayed refresh so the user sees the new counts sooner.
        window.setTimeout(() => qc.invalidateQueries(), 120_000);
      } else {
        toast.success(`Scrape complete: ${data.property_count} records from ${data.ok_count}/${data.source_count} sources.`);
        qc.invalidateQueries();
      }
    },
    onError: (err) => {
      const msg = err?.response?.data?.detail || err.message || "Scrape failed";
      toast.error(msg);
    },
  });

  const latest = stats?.latest_run;
  const topStates = stats?.top_states || [];

  return (
    <div data-testid={DASHBOARD.root} className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="data-label">Overview</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-red-600">
            Lien Scout
          </h1>
          <p className="font-mono-data text-[11px] uppercase tracking-[0.18em] text-yellow-700 mt-3">
            AI-Powered Property Intelligence
          </p>
          <p className="text-sm text-zinc-600 mt-3 max-w-xl">
            Nationwide public tax-lien and tax-sale coverage. Auto-refreshes
            every 30 minutes; filter down to the top 5 cities per state and
            export the leads.
          </p>
        </div>
        <BrutalButton
          data-testid={DASHBOARD.runAllBtn}
          onClick={() => runAll.mutate()}
          disabled={runAll.isPending}
        >
          <Play size={16} strokeWidth={2.5} />
          {runAll.isPending ? "Scraping…" : "Run All Sources"}
        </BrutalButton>
      </div>

      {/* Stats bento */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          testid={DASHBOARD.statTotal}
          label="Total Properties"
          value={(stats?.properties_total ?? 0).toLocaleString()}
          sub="deduplicated across all runs"
          icon={Table2}
        />
        <StatCard
          testid={DASHBOARD.statSources}
          label="Active Sources"
          value={`${stats?.sources_enabled ?? 0}/${stats?.sources_total ?? 0}`}
          sub="enabled / configured"
          icon={Database}
        />
        <StatCard
          testid={DASHBOARD.statRuns}
          label="Scrape Runs"
          value={(stats?.runs_total ?? 0).toLocaleString()}
          sub="historical"
          icon={Activity}
        />
        <StatCard
          testid={DASHBOARD.statAmount}
          label="Total Amount Owed"
          value={fmtMoney(stats?.total_amount_owed || 0)}
          sub="sum across properties"
          icon={DollarSign}
        />
      </div>

      {/* Latest run + top states */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <BentoCard
          data-testid={DASHBOARD.latestRunCard}
          className="lg:col-span-2"
          accent
        >
          <div className="flex items-center justify-between">
            <span className="data-label">Latest Run</span>
            {latest ? (
              latest.fail_count > 0 ? (
                <HexBadge tone="warning">Partial</HexBadge>
              ) : (
                <HexBadge tone="success">Complete</HexBadge>
              )
            ) : (
              <HexBadge>Idle</HexBadge>
            )}
          </div>
          {latest ? (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
              <MiniStat label="Finished" value={fmtDateTime(latest.finished_at)} mono />
              <MiniStat label="Sources" value={`${latest.ok_count}/${latest.source_count}`} />
              <MiniStat label="Records" value={latest.property_count} />
              <MiniStat label="Errors" value={latest.fail_count} />
            </div>
          ) : (
            <div className="mt-4 py-8 text-center">
              <Hexagon size={48} className="mx-auto text-yellow-500" strokeWidth={1.5} />
              <p className="mt-3 font-display font-semibold text-lg">No runs yet</p>
              <p className="text-sm text-zinc-500">Kick off your first scrape with “Run All Sources”.</p>
            </div>
          )}
          <div className="mt-4">
            <Link to="/runs" className="inline-flex items-center gap-1 text-sm font-semibold underline decoration-yellow-500 decoration-2 underline-offset-4 hover:text-yellow-700">
              View full run history <ArrowUpRight size={14} />
            </Link>
          </div>
        </BentoCard>

        <BentoCard data-testid={DASHBOARD.topStatesCard}>
          <span className="data-label">Top States</span>
          <div className="mt-4 space-y-3">
            {topStates.length === 0 && (
              <p className="text-sm text-zinc-500">No properties scraped yet.</p>
            )}
            {topStates.map((row) => (
              <div key={row.state} className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Hexagon size={14} className="text-yellow-500 fill-yellow-100" strokeWidth={2} />
                  <span className="font-mono-data font-semibold">{row.state}</span>
                </div>
                <span className="font-display font-bold text-lg">{row.count}</span>
              </div>
            ))}
          </div>
        </BentoCard>
      </div>

      {/* Deeper analytics: sale-date histogram + top counties by $ owed */}
      <DashboardAnalytics />
    </div>
  );
}

function MiniStat({ label, value, mono }) {
  return (
    <div>
      <div className="data-label">{label}</div>
      <div className={`mt-1 font-semibold text-sm ${mono ? "font-mono-data" : "font-display"}`}>
        {value ?? "—"}
      </div>
    </div>
  );
}
